import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";

function fmtDateTime(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, color: "#7A7468", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, marginBottom: 10 }}>
      {children}
    </div>
  );
}

export default function AdminCommunication() {
  const [architects, setArchitects] = useState([]);
  const [archLoading, setArchLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // null | "success" | "error"
  const [sendError, setSendError] = useState("");
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    supabase.from("profiles").select("id, name, email").eq("role", "architect").order("name")
      .then(({ data }) => { setArchitects(data || []); setArchLoading(false); });
  }, []);

  const loadHistory = useCallback(async () => {
    const { data } = await supabase
      .from("admin_emails")
      .select("id, sent_at, subject, recipients, body")
      .order("sent_at", { ascending: false })
      .limit(50);
    setHistory(data || []);
    setHistLoading(false);
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const allSelected = architects.length > 0 && architects.every(a => selected.has(a.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(architects.map(a => a.id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || selected.size === 0) return;
    setSending(true);
    setSendResult(null);
    setSendError("");

    const { data: { session } } = await supabase.auth.getSession();
    const recipients = architects
      .filter(a => selected.has(a.id))
      .map(a => ({ id: a.id, name: a.name || null, email: a.email || null }));

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            recipients,
            subject: subject.trim(),
            body: body.trim(),
            sent_by: session.user.id,
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Erreur HTTP ${resp.status}`);
      }
      setSendResult("success");
      setSubject("");
      setBody("");
      setSelected(new Set());
      loadHistory();
    } catch (e) {
      setSendResult("error");
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && selected.size > 0 && !sending;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400 }}>Communication</h1>
        <p style={{ fontSize: 13, color: "#7A7468", marginTop: 4 }}>Envoyer des emails aux architectes de la plateforme</p>
      </div>

      {/* ── Compose card ── */}
      <div style={{ background: "#1A1916", border: "1px solid #2A2924", borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E2D8", marginBottom: 22 }}>Composer un message</div>

        {/* Recipients */}
        <div style={{ marginBottom: 22 }}>
          <SectionLabel>Destinataires</SectionLabel>

          {/* Select-all chip */}
          <button
            className="btn"
            onClick={toggleAll}
            style={{
              padding: "6px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600, marginBottom: 12,
              border: `1px solid ${allSelected ? "#C9A96E60" : "#2A2924"}`,
              background: allSelected ? "#C9A96E18" : "#252420",
              color: allSelected ? "#C9A96E" : "#7A7468",
              transition: "all .15s",
            }}
          >
            {allSelected ? "✓ " : ""}Tous les architectes ({architects.length})
          </button>

          {/* Individual chips */}
          {archLoading ? (
            <div className="spinner" />
          ) : architects.length === 0 ? (
            <span style={{ fontSize: 13, color: "#5A5450" }}>Aucun architecte enregistré</span>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {architects.map(a => {
                const on = selected.has(a.id);
                return (
                  <button
                    key={a.id}
                    className="btn"
                    onClick={() => toggleOne(a.id)}
                    style={{
                      padding: "5px 12px", borderRadius: 99, fontSize: 12,
                      border: `1px solid ${on ? "#7EB8C960" : "#2A2924"}`,
                      background: on ? "#7EB8C918" : "transparent",
                      color: on ? "#7EB8C9" : "#7A7468",
                      fontWeight: on ? 600 : 400,
                      transition: "all .15s",
                    }}
                  >
                    {on ? "✓ " : ""}{a.name || a.email || a.id.slice(0, 8)}
                  </button>
                );
              })}
            </div>
          )}

          {someSelected && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#C9A96E" }}>
              {selected.size} destinataire{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
            </div>
          )}
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 16 }}>
          <SectionLabel>Sujet</SectionLabel>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Objet de l'email"
            style={{ fontSize: 13 }}
          />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 22 }}>
          <SectionLabel>Message</SectionLabel>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Corps du message…"
            rows={7}
            style={{ fontSize: 13, resize: "vertical" }}
          />
        </div>

        {/* Status feedback */}
        {sendResult === "success" && (
          <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "#A8C97E12", border: "1px solid #A8C97E40", color: "#A8C97E", fontSize: 13 }}>
            ✓ Email envoyé avec succès
          </div>
        )}
        {sendResult === "error" && (
          <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "#FC818112", border: "1px solid #FC818140", color: "#FC8181", fontSize: 13 }}>
            ❌ {sendError || "Une erreur est survenue"}
          </div>
        )}

        {/* Send button */}
        <button
          className="btn"
          onClick={handleSend}
          disabled={!canSend}
          style={{
            padding: "11px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, border: "none",
            background: canSend ? "#C9A96E" : "#252420",
            color: canSend ? "#0F0E0C" : "#5A5450",
            cursor: canSend ? "pointer" : "default",
            transition: "all .15s",
          }}
        >
          {sending
            ? "Envoi en cours…"
            : selected.size > 0
              ? `Envoyer à ${selected.size} destinataire${selected.size > 1 ? "s" : ""}`
              : "Envoyer"}
        </button>
      </div>

      {/* ── History ── */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E2D8", marginBottom: 16 }}>Historique des envois</div>
        <div style={{ background: "#1A1916", border: "1px solid #2A2924", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #2A2924" }}>
                {["Date", "Sujet", "Destinataires", ""].map((h, i) => (
                  <th key={i} style={{ padding: "12px 16px", textAlign: "left", fontSize: 10, color: "#7A7468", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {histLoading ? (
                <tr><td colSpan={4} style={{ padding: 40, textAlign: "center" }}>
                  <div className="spinner" style={{ margin: "0 auto" }} />
                </td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 40, textAlign: "center", color: "#7A7468", fontSize: 13 }}>
                  Aucun email envoyé pour l'instant
                </td></tr>
              ) : history.map((item, i) => {
                const recipCount = Array.isArray(item.recipients) ? item.recipients.length : 0;
                const isExpanded = expandedId === item.id;
                return (
                  <>
                    <tr key={item.id} style={{ borderBottom: "1px solid #1F1E1A" }}>
                      <td style={{ padding: "13px 16px", fontSize: 12, color: "#7A7468", whiteSpace: "nowrap" }}>{fmtDateTime(item.sent_at)}</td>
                      <td style={{ padding: "13px 16px", fontSize: 13, color: "#E8E2D8", fontWeight: 500 }}>{item.subject}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <span style={{ fontSize: 12, color: "#7A7468" }}>
                          {recipCount} destinataire{recipCount !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td style={{ padding: "13px 16px" }}>
                        <button
                          className="btn"
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          style={{ fontSize: 11, color: "#7A7468", border: "1px solid #2A2924", borderRadius: 6, padding: "4px 10px", background: "transparent" }}
                        >
                          {isExpanded ? "Masquer" : "Voir"}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${item.id}-body`} style={{ borderBottom: i < history.length - 1 ? "1px solid #1F1E1A" : "none" }}>
                        <td colSpan={4} style={{ padding: "0 16px 16px" }}>
                          <div style={{ background: "#252420", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#B0AA9E", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                            {item.body}
                          </div>
                          {Array.isArray(item.recipients) && item.recipients.length > 0 && (
                            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {item.recipients.map((r, ri) => (
                                <span key={ri} className="chip" style={{ fontSize: 11 }}>
                                  {r.name || r.email || r.id}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
